// Phase T3.3 — Public extraction-intelligence record (pure).
//
// Advancement_v3_0 §T3.3. The CLV eval loop becomes a front-facing
// trust mechanism: "here is the CLV record of every extraction we
// have processed — verify it yourself." All aggregate, no user data.
//
// Zero I/O. Shapes the Edge-fn payload into a view model + the
// honest headline string. N08: when there is no record yet, the
// copy says so — it never fabricates a number.

export interface ExtractionStatsPayload {
  total_all_time: number;
  total_30d: number;
  /** Share (0..1) of trailing-30d extractions with positive kept CLV. */
  positive_clv_share_30d: number | null;
  /** Mean CLV improvement (proportion) of kept subset vs original. */
  avg_clv_improvement_30d: number | null;
  by_sport: Record<string, { n: number; avg_clv: number }>;
  by_market: Record<string, { n: number; avg_clv: number }>;
  generated_at: string;
}

export interface ExtractionStatsView {
  hasRecord: boolean;
  totalAllTime: number;
  total30d: number;
  positivePct30d: number | null;
  avgClvPct30d: number | null;
  sports: { key: string; n: number; avgClvPct: number }[];
  markets: { key: string; n: number; avgClvPct: number }[];
  /** Honest one-line trust statement for the marketing surface +
   *  PaywallCard. Never overstated; says "accumulating" when thin. */
  headline: string;
  generatedAt: string;
}

function pctFrac(v: number | null): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v * 100 : null;
}

function rankBreakdown(
  b: Record<string, { n: number; avg_clv: number }> | null | undefined,
): { key: string; n: number; avgClvPct: number }[] {
  if (!b || typeof b !== "object") return [];
  return Object.entries(b)
    .map(([key, v]) => ({
      key,
      n: Number(v?.n) || 0,
      avgClvPct: Number.isFinite(Number(v?.avg_clv))
        ? Number(v.avg_clv) * 100
        : 0,
    }))
    .filter((r) => r.n > 0)
    .sort((a, b2) => b2.n - a.n);
}

/** Minimum trailing-30d sample before we state a CLV rate publicly
 *  (Roadmap precondition #2 — don't market a number the data can't
 *  yet support). */
export const PUBLIC_STAT_MIN_SAMPLE = 20;

export function shapeExtractionStats(
  p: ExtractionStatsPayload | null | undefined,
): ExtractionStatsView {
  const empty: ExtractionStatsView = {
    hasRecord: false,
    totalAllTime: 0,
    total30d: 0,
    positivePct30d: null,
    avgClvPct30d: null,
    sports: [],
    markets: [],
    headline:
      "STRIKZA's extraction CLV record is accumulating — published here " +
      "as soon as there is a verifiable sample.",
    generatedAt: "",
  };
  if (!p || typeof p !== "object") return empty;

  const totalAllTime = Number(p.total_all_time) || 0;
  const total30d = Number(p.total_30d) || 0;
  const positivePct30d = pctFrac(p.positive_clv_share_30d);
  const avgClvPct30d = pctFrac(p.avg_clv_improvement_30d);

  if (totalAllTime === 0) return { ...empty, generatedAt: p.generated_at ?? "" };

  const enoughForRate = total30d >= PUBLIC_STAT_MIN_SAMPLE &&
    positivePct30d !== null;

  const headline = enoughForRate
    ? `${positivePct30d!.toFixed(0)}% of extractions produced positive ` +
      `closing-line value in the last 30 days ` +
      `(${total30d.toLocaleString()} extractions, verifiable).`
    : `${totalAllTime.toLocaleString()} extractions processed. The ` +
      `trailing-30-day CLV rate publishes once the sample is large ` +
      `enough to state honestly.`;

  return {
    hasRecord: true,
    totalAllTime,
    total30d,
    positivePct30d,
    avgClvPct30d,
    sports: rankBreakdown(p.by_sport),
    markets: rankBreakdown(p.by_market),
    headline,
    generatedAt: p.generated_at ?? "",
  };
}
